/** @format */
import {useEffect} from 'react';

import {MonitorOutlined, SensorsOutlined, TornadoOutlined} from '@mui/icons-material';

import {useNavigate} from 'react-router-dom';

import {useIsScrolling} from 'helpers/hooks/useIsScrolling';

import mediaplanBg from '../../images/anim_mediaplan.svg';
import funnelBg from '../../images/anim_finnel.svg';
import feedsBg from '../../images/anim_feeds.svg';
import sensorsBg from '../../images/anim_sensors.svg';
import {HotspotBig} from '../../components/HotspotBig';
import {LearnVideo} from '../../components/LearnVideo';
import {Container, Content, FadeBox, Root, Scroller} from './styles';
import {HotspotKey, HotspotStatusList} from '../../../../types';
import {getHotspotVariant} from '../../helpers/getHotspotVariant';
import {RenderAlertComponent} from './RenderAlertComponent';
import {HOTSPOT_HEADERS} from '../constants';

interface Props {
  statusList: HotspotStatusList;
  onHide: (key: HotspotKey) => void;
  onSetup: (key: HotspotKey) => void;
  companyId: string;
  daysLeft: number;
  isTrial: boolean;
}

const HotspotLayoutBig = ({statusList, daysLeft, isTrial, onHide, companyId, onSetup}: Props) => {
  const navigate = useNavigate();

  const {scrollerRef, isScrolling, checkScrolling} = useIsScrolling();

  useEffect(() => {
    checkScrolling();
  }, [statusList, checkScrolling]);

  const keys = Object.keys(statusList) as HotspotKey[];

  const filteredKeys = keys.filter(key => {
    const {isDisplay, isReady} = statusList[key];
    return !isReady && isDisplay;
  });

  if (!filteredKeys.length) {
    return null;
  }

  return (
    <Root>
      <Container>
        <Scroller ref={scrollerRef}>
          <Content>
            {filteredKeys.map((key, index) => {
              const isHighlight = index === 0;

              switch (key) {
                case 'mediaplan':
                  return (
                    <HotspotBig
                      key={key}
                      content={{
                        headerText: HOTSPOT_HEADERS.mediaplan,
                        subHeaderText: 'Первым делом',
                        HeaderIcon: <MonitorOutlined />,
                        description: [
                          'Узнайте, куда уходят деньги и что реально\nприносит результат. Настройте бюджет и KPI чтобы увидеть:',
                        ],
                        chips: [
                          'Связь трат и результатов',
                          'План-факт и прогнозы',
                          'Аномалии и рекомендации',
                        ],
                        RightColumnComponent: <LearnVideo />,
                        buttonText: 'Настроить',
                        AlertComponent: daysLeft >= 1 && daysLeft <= 2 && isTrial && (
                          <RenderAlertComponent hotspotKey='mediaplan' daysLeft={daysLeft} />
                        ),
                      }}
                      isHighlight={isHighlight}
                      variant={getHotspotVariant({
                        daysLeft,
                        isTrial,
                        isHighlight,
                        hotspotKey: 'mediaplan',
                      })}
                      bgImage={mediaplanBg}
                      hotspotKey='mediaplan'
                      onHide={onHide}
                      onSetup={() => {
                        onSetup('mediaplan');
                        navigate(`/home/project/periods?companyId=${companyId}&edit=1`);
                      }}
                      bgOpacity={1}
                    />
                  );
                case 'funnel':
                  return (
                    <HotspotBig
                      key={key}
                      content={{
                        headerText: HOTSPOT_HEADERS.funnel,
                        HeaderIcon: <TornadoOutlined />,
                        description: [
                          'Определите в чем проблема, на сайте или в рекламе. Сравнивайте воронку с KPI и действиями в кабинете.',
                        ],
                        chips: [
                          'Всплески и провалы',
                          'Просадки конверсии',
                          'Этапы воронки на общем таймлайне',
                        ],
                        buttonText: 'Настроить',
                      }}
                      isHighlight={isHighlight}
                      variant={getHotspotVariant({
                        daysLeft,
                        isTrial,
                        isHighlight,
                        hotspotKey: 'funnel',
                      })}
                      bgImage={funnelBg}
                      hotspotKey='funnel'
                      onHide={onHide}
                      onSetup={() => {
                        onSetup('funnel');
                        navigate(`/home/project/funnel?companyId=${companyId}&edit=1`);
                      }}
                      bgOpacity={0.4}
                    />
                  );
                case 'sensors':
                  return (
                    <HotspotBig
                      key={key}
                      content={{
                        headerText: HOTSPOT_HEADERS.sensors,
                        HeaderIcon: <SensorsOutlined />,
                        description: [
                          'Наша уникальная система мониторинга качества кампаний проверяет сотни параметров ежедневно.',
                          'Настройте приоритеты под себя:',
                        ],
                        chips: [
                          'Сигналы только по важным проблемам',
                          'Быстрая реакция на аномалии',
                          'Кампании под контролем без стресса',
                        ],
                        buttonText: 'Распределить',
                        AlertComponent: daysLeft <= 0 && !isTrial && (
                          <RenderAlertComponent hotspotKey='sensors' daysLeft={1} />
                        ),
                      }}
                      isHighlight={isHighlight}
                      variant={getHotspotVariant({
                        daysLeft,
                        isTrial,
                        isHighlight,
                        hotspotKey: 'sensors',
                      })}
                      bgImage={sensorsBg}
                      hotspotKey='sensors'
                      onHide={onHide}
                      onSetup={() => {
                        onSetup('sensors');
                        navigate(`/home/project/sensors?companyId=${companyId}&edit=1`);
                      }}
                      bgOpacity={1}
                    />
                  );
                case 'feeds':
                  return (
                    <HotspotBig
                      key={key}
                      content={{
                        headerText: HOTSPOT_HEADERS.feeds,
                        subHeaderText: 'Настройте, чтобы начать отслеживать',
                        description: [
                          'Следите за ассортиментом и не теряйте продажи.\nСенсоры проверяют всё важное:',
                        ],
                        chips: [
                          'Наличие ключевых товаров',
                          'Актуальность данных',
                          'Ошибки и аномалии в фидах',
                        ],
                        buttonText: 'Настроить',
                      }}
                      isHighlight={isHighlight}
                      variant={getHotspotVariant({
                        daysLeft,
                        isTrial,
                        isHighlight,
                        hotspotKey: 'feeds',
                      })}
                      bgImage={feedsBg}
                      hotspotKey='feeds'
                      onHide={onHide}
                      onSetup={() => {
                        onSetup('feeds');
                        navigate(`/home/project/feeds?companyId=${companyId}&edit=1`);
                      }}
                      bgOpacity={0.4}
                    />
                  );
                default:
                  return null;
              }
            })}
          </Content>
        </Scroller>

        {isScrolling && <FadeBox />}
      </Container>
    </Root>
  );
};

export {HotspotLayoutBig};
